import { PRODUCT_KINDS, PRODUCT_SCOPE_TYPES, type ProductDto } from '@/types/dtos/orders/response/product.dto';
import type { QuotaBalanceDto } from '@/types/dtos/entitlements/quota-balance.dto';
import type { TranslationKey } from '@/i18n/translation-key.tsx';
import {
  CATALOG_FALLBACK_QUOTA_KEY_BY_PRODUCT_KEY,
  getProductCatalogEntry,
  type QuotaAvatarConfig,
} from '@/constants/product-catalog.ts';
import { getOriginEventId } from '@/helpers/event.helper';
import { resolveRefId } from '@/helpers/object.id.helper';

export function isQuotaProductKind(kind: ProductDto['kind'] | string | undefined): boolean {
  if (kind === undefined || kind === null) return false;
  return String(kind).toUpperCase() === PRODUCT_KINDS.QUOTA;
}

/** Minimal event shape for contributor/upload quota balance scope resolution. */
export type EventQuotaScopeInput = {
  id?: string;
  origin?: string | { id: string };
  multiFlow?: boolean;
  flows?: Array<{ id: string; origin?: unknown }>;
};

export type EventQuotaScope = {
  scopeType: typeof PRODUCT_SCOPE_TYPES.EVENT | typeof PRODUCT_SCOPE_TYPES.FLOW;
  scopeId: string | undefined;
};

/**
 * Contributor / upload quota balances live on Flow for single-flow events (`base_capacity`)
 * and on Event for multi-flow (`base_capacity_event`). Prefer Flow when a primary flow id is
 * present; fall back to Event when flows are missing from the payload.
 */
export function resolveQuotaScopeForEvent(event?: EventQuotaScopeInput): EventQuotaScope {
  const originEventId = getOriginEventId(event);
  if (!event || event.multiFlow === true) {
    return { scopeType: PRODUCT_SCOPE_TYPES.EVENT, scopeId: originEventId };
  }
  const primaryFlow = event.flows?.[0];
  const singleFlowId = primaryFlow
    ? (resolveRefId(primaryFlow.origin as Parameters<typeof resolveRefId>[0]) ?? primaryFlow.id)
    : undefined;
  if (singleFlowId) {
    return { scopeType: PRODUCT_SCOPE_TYPES.FLOW, scopeId: singleFlowId };
  }
  return { scopeType: PRODUCT_SCOPE_TYPES.EVENT, scopeId: originEventId };
}

/**
 * Resolves the entitlement / balance `quotaKey` for a catalog product.
 * Uses `quotaConfig.quotaKey` when present; otherwise known defaults for event SKU keys
 * when `kind` is QUOTA (case-insensitive).
 */
export function getQuotaKeyForProduct(product: ProductDto): string | undefined {
  const fromConfig = product.quotaConfig?.quotaKey;
  if (fromConfig) return fromConfig;
  if (!isQuotaProductKind(product.kind)) return undefined;
  const key = product.key as string;
  return CATALOG_FALLBACK_QUOTA_KEY_BY_PRODUCT_KEY[key];
}

function mergeQuotaAvatarConfig(product: ProductDto): QuotaAvatarConfig {
  const key = String(product.key);
  return { visible: true, ...(getProductCatalogEntry(key)?.quotaAvatar ?? {}) };
}

/** Whether `EventCatalogProductQuotas` should render for this product (default true). */
export function isQuotaAvatarVisible(product: ProductDto): boolean {
  return mergeQuotaAvatarConfig(product).visible !== false;
}

/**
 * Balance `quotaKey` for the catalog quota avatar: explicit `balanceQuotaKey` from catalog config,
 * otherwise the same resolution as checkout / entitlements for the product.
 */
export function getQuotaKeyForCatalogAvatar(product: ProductDto): string | undefined {
  const override = mergeQuotaAvatarConfig(product).balanceQuotaKey;
  if (override) return override;
  return getQuotaKeyForProduct(product);
}

/**
 * Picks the store `products` row for a monetized SKU (`key` + `scopeType`), e.g. Event-scoped `event_contributor`.
 */
export function findProductInStoreByKeyAndScope(
  products: ProductDto[] | undefined,
  productKey: string,
  scopeType: string = PRODUCT_SCOPE_TYPES.EVENT,
): ProductDto | undefined {
  if (!products?.length) return undefined;
  const scopeLc = String(scopeType).toLowerCase();
  const keyNorm = String(productKey);
  return products.find((p) => String(p.key) === keyNorm && String(p.scopeType).toLowerCase() === scopeLc);
}

/**
 * For info alerts, deficit checks, and invite gating: resolve the sellable product row and the `quota_balances`
 * row key to use. Uses the same rules as catalog quota avatars (`balanceQuotaKey` for PACKAGE rows that proxy
 * another balance, then `quotaConfig` / QUOTA fallbacks) via {@link getQuotaKeyForCatalogAvatar}.
 */
export type ScopedProductBalanceContext = {
  product: ProductDto;
  /** Match against `QuotaBalanceDto.quotaKey`; undefined when no balance row applies */
  quotaKey: string | undefined;
};

export function resolveScopedProductBalanceContext(
  products: ProductDto[] | undefined,
  productKey: string,
  scopeType: string = PRODUCT_SCOPE_TYPES.EVENT,
): ScopedProductBalanceContext | undefined {
  const product = findProductInStoreByKeyAndScope(products, productKey, scopeType);
  if (!product) return undefined;
  return { product, quotaKey: getQuotaKeyForCatalogAvatar(product) };
}

export function findQuotaBalanceForScopedProductContext(
  balances: QuotaBalanceDto[] | undefined,
  context: ScopedProductBalanceContext | undefined,
): QuotaBalanceDto | undefined {
  if (!context?.quotaKey || !balances?.length) return undefined;
  return balances.find((b) => b.quotaKey === context.quotaKey);
}

const DEFAULT_QUOTA_AVATAR_TOOLTIP_KEY: TranslationKey = 'event.features.contributorsUsed';

export function getQuotaAvatarTooltipTranslationKey(product: ProductDto): TranslationKey {
  const cfg = mergeQuotaAvatarConfig(product);
  if (cfg.tooltipKey) return cfg.tooltipKey;
  const entry = getProductCatalogEntry(String(product.key));
  return entry?.quotaUsedTooltipKey ?? DEFAULT_QUOTA_AVATAR_TOOLTIP_KEY;
}
